/**
 * FILE: family-plan-checkout.js
 * PURPOSE: Keep cart in-sync in the background; instant redirect to /checkout
 * VERSION: 3.0 – Background cart sync + flush-on-checkout
 */
(function() {
  'use strict';

  // ---------- utilities ----------
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  function waitForElements(selectorOrList, { timeout = 8000 } = {}) {
    const selectors = Array.isArray(selectorOrList) ? selectorOrList : [selectorOrList];
    return new Promise((resolve) => {
      const found = () => selectors.some(sel => !!document.querySelector(sel));
      if (found()) return resolve();

      const obs = new MutationObserver(() => { if (found()) done(); });
      const timer = setTimeout(() => { obs.disconnect(); resolve(); }, timeout);
      function done() { clearTimeout(timer); obs.disconnect(); resolve(); }

      obs.observe(document.documentElement, { childList: true, subtree: true });
      document.addEventListener('DOMContentLoaded', () => { if (found()) done(); });
    });
  }

  function onShopifySectionEvents(cb) {
    [
      'shopify:section:load','shopify:section:unload','shopify:section:select',
      'shopify:section:deselect','shopify:block:select','shopify:block:deselect'
    ].forEach(evt => document.addEventListener(evt, () => setTimeout(cb, 0)));
  }

  // ---------- module ----------
  const FamilyPlanCheckout = {
    booted: false,

    // state for background sync
    _debounceTimer: null,
    _debounceMs: 600,
    _isSyncing: false,
    _pendingImmediate: false,
    _lastSyncedHash: '',

    init() {
      if (this.booted) return;
      this.booted = true;
      console.log('✅ Family Plan Checkout: Loaded');

      this.bindEvents();
      this.bindBuilderChangeDetection();

      // first render
      setTimeout(() => this.updateSummary(), 200);
      // initial background sync (does nothing if empty)
      this.queueSync();
    },

    reinit() {
      this.booted = false;
      this.init();
    },

    bindEvents() {
      const desktopBtn = document.getElementById('checkoutBtn');
      const mobileBtn  = document.getElementById('mobileCheckoutBtn');

      if (desktopBtn && !desktopBtn.dataset.fpcBound) {
        desktopBtn.addEventListener('click', this.handleCheckout.bind(this));
        desktopBtn.dataset.fpcBound = '1';
        console.log('✅ Desktop button bound');
      }
      if (mobileBtn && !mobileBtn.dataset.fpcBound) {
        mobileBtn.addEventListener('click', this.handleCheckout.bind(this));
        mobileBtn.dataset.fpcBound = '1';
        console.log('✅ Mobile button bound');
      }
    },

    // Listen to qty +/- and radio changes (event delegation)
    bindBuilderChangeDetection() {
      const builder = document.getElementById('familyBuilder');
      if (!builder) return;

      const onChange = () => {
        this.updateSummary();
        this.queueSync();
      };

      if (!builder.dataset.fpcEvents) {
        builder.addEventListener('click', (e) => {
          const t = e.target;
          if (t && t.classList && (t.classList.contains('stepper-btn') || t.classList.contains('stepper-input'))) {
            onChange();
          }
        });
        builder.addEventListener('change', (e) => {
          const t = e.target;
          if (t && t.matches('input[type="radio"]')) {
            onChange();
          }
        });
        builder.dataset.fpcEvents = '1';
      }
    },

    // Build items from UI (selling plan optional)
    collectItems: function(opts = {}) {
      const { strict = false } = opts;
      const builder = document.getElementById('familyBuilder');
      if (!builder) { if (strict) console.error('❌ Builder element not found'); return []; }

      const cards = builder.querySelectorAll('.blend-card');
      const items = [];
      let hasError = false;

      cards.forEach((card, index) => {
        const qtyEl = card.querySelector('.stepper-input');
        const qty = parseInt(qtyEl ? qtyEl.value : '0', 10);
        if (!qty) return;

        const selectedRadio = card.querySelector('input[type="radio"]:checked');
        if (!selectedRadio) { if (strict) this.showToast(`Kies een plan voor kaart ${index + 1}`, 'error'); hasError = true; return; }

        // Variant ID (accept gid or number; use trailing digits)
        const rawVariant = (selectedRadio.dataset.variant || '').trim();
        const match = rawVariant.match(/(\d+)$/);
        const variantId = match ? Number(match[1]) : NaN;
        if (!Number.isInteger(variantId)) { if (strict) this.showToast(`Variant ID ongeldig (kaart ${index + 1})`, 'error'); hasError = true; return; }

        // Optional selling plan (only if present & numeric)
      const rawPlan = (selectedRadio.dataset.sellingPlan || '').trim();
        const sellingPlanId = rawPlan ? Number(rawPlan) : null;
        if (rawPlan && !Number.isInteger(sellingPlanId)) { if (strict) this.showToast(`Selling plan ID ongeldig (kaart ${index + 1})`, 'error'); hasError = true; return; }

        const item = {
          id: variantId,
          quantity: qty,
          properties: { '_blend': card.dataset.blend || '', '_plan': selectedRadio.value || '' }
        };
        if (sellingPlanId) item.selling_plan = sellingPlanId;

        items.push(item);
      });

      if (strict && (hasError || items.length === 0)) {
        if (!hasError) this.showToast('Selecteer minimaal één product om door te gaan.', 'error');
      }

      return hasError ? [] : items;
    },

    // --- background cart sync (debounced) ---
    queueSync() {
      const desired = this.collectItems({ strict: false });
      const hash = JSON.stringify(desired);

      // if builder has invalid/missing data, skip syncing quietly
      if (!Array.isArray(desired)) return;

      if (hash === this._lastSyncedHash && !this._isSyncing) return;

      this._pendingImmediate = false;
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.syncCartInBackground(), this._debounceMs);
    },

    // force immediate sync (used before checkout)
    async flushSync() {
      if (this._isSyncing) {
        while (this._isSyncing) { await sleep(50); }
        return;
      }
      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = null;
        await this.syncCartInBackground();
      }
    },

    async syncCartInBackground() {
      if (this._isSyncing) { this._pendingImmediate = true; return; }
      this._isSyncing = true;

      try {
        const items = this.collectItems({ strict: false });
        const desiredHash = JSON.stringify(items);
        if (desiredHash === this._lastSyncedHash) return;

        const invalid = items.some(l => !Number.isInteger(l.id) || !Number.isInteger(l.quantity) || l.quantity <= 0);
        if (invalid) { console.warn('⚠️ Skipping sync: invalid items', items); return; }

        console.log('🧩 Background sync payload:', items);

        await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin'
        });

        if (items.length > 0) {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items })
          });

          const body = await res.text();
          if (!res.ok) {
            console.error('❌ Background /cart/add.js 422 body:', body);
            return;
          }
          console.log('✅ Background /cart/add.js OK:', body);
        }

        this._lastSyncedHash = desiredHash;
      } catch (e) {
        console.error('❌ Background sync failed:', e);
      } finally {
        this._isSyncing = false;
        if (this._pendingImmediate) {
          this._pendingImmediate = false;
          this.syncCartInBackground();
        }
      }
    },

    // --- checkout ---
    async handleCheckout(event) {
      event.preventDefault();
      const button = event.currentTarget;

      const itemsNow = this.collectItems({ strict: true });
      if (itemsNow.length === 0) return;

      this.setButtonLoading(button, true);
      try {
        await this.flushSync();
        await new Promise(r => setTimeout(r, 200));
        window.location.href = '/checkout';
      } catch (error) {
        console.error('❌ Checkout redirect failed:', error);
        this.setButtonLoading(button, false);
        this.showToast('Er is iets misgegaan met afrekenen. Probeer opnieuw.', 'error');
      }
    },

    setButtonLoading(button, isLoading) {
      if (!button) return;
      if (isLoading) {
        button.disabled = true;
        button.dataset.originalText = button.textContent;
        button.textContent = 'Laden...';
        button.style.opacity = '0.7';
      } else {
        button.disabled = false;
        button.textContent = button.dataset.originalText || 'Doorgaan naar afrekenen';
        button.style.opacity = '1';
      }
    },

    showToast(message, type = 'error') {
      document.querySelectorAll('.family-plan-toast').forEach(t => t.remove());
      const toast = document.createElement('div');
      toast.className = `family-plan-toast family-plan-toast--${type}`; // fixed
      toast.textContent = message;
      document.body.appendChild(toast);
      setTimeout(() => toast.classList.add('show'), 10);
      setTimeout(() => { toast.classList.remove('show'); setTimeout(() => toast.remove(), 300); }, 5000);
    },

    updateSummary() {
      const items = this.collectItems({ strict: false });
      const summaryItems = document.getElementById('summaryItems');
      const summaryTotal = document.getElementById('summaryTotal');
      const mobileSummaryTotal = document.getElementById('mobileSummaryTotal');
      const checkoutBtn = document.getElementById('checkoutBtn');
      const mobileCheckoutBtn = document.getElementById('mobileCheckoutBtn');

      if (!summaryItems) return;

      if (items.length === 0) {
        summaryItems.innerHTML = '<p class="checkout-summary__empty">Selecteer een Plan om te beginnen</p>';
        if (summaryTotal) summaryTotal.textContent = '€0';
        if (mobileSummaryTotal) mobileSummaryTotal.textContent = '€0';
        if (checkoutBtn) checkoutBtn.disabled = true;
        if (mobileCheckoutBtn) mobileCheckoutBtn.disabled = true;
        return;
      }

      // compute display from DOM to keep prices local
      let total = 0;
      const builder = document.getElementById('familyBuilder');
      const cards = builder ? builder.querySelectorAll('.blend-card') : [];
      const summaryHTML = [];

      cards.forEach(card => {
        const qtyEl = card.querySelector('.stepper-input');
        const qty = parseInt(qtyEl ? qtyEl.value : '0', 10);
        if (qty > 0) {
          const selectedRadio = card.querySelector('input[type="radio"]:checked');
          if (!selectedRadio) return;
          const titleEl = card.querySelector('.blend-card__title');
          const title = titleEl ? titleEl.textContent : '';
          const planTitleEl = selectedRadio.parentElement.querySelector('.plan-radio__title');
          const planText = planTitleEl ? planTitleEl.textContent : '';
          const price = parseFloat(selectedRadio.dataset.price);
          const itemTotal = (isNaN(price) ? 0 : price) * qty;

          total += itemTotal;
          summaryHTML.push(`
            <div class="summary-item">
              <div class="summary-item__details">
                <div class="summary-item__name">${title}</div>
                <div class="summary-item__meta">${qty}× ${planText}</div>
              </div>
              <div class="summary-item__price">€${itemTotal.toFixed(2)}</div>
            </div>
          `);
        }
      });

      summaryItems.innerHTML = summaryHTML.join('');
      if (summaryTotal) summaryTotal.textContent = `€${total.toFixed(2)}`;
      if (mobileSummaryTotal) mobileSummaryTotal.textContent = `€${total.toFixed(2)}`;
      if (checkoutBtn) checkoutBtn.disabled = false;
      if (mobileCheckoutBtn) mobileCheckoutBtn.disabled = false;
    },

    debugInfo() {
      console.log('=== FAMILY PLAN DEBUG INFO ===');
      console.log('Builder exists:', !!document.getElementById('familyBuilder'));
      console.log('Summary exists:', !!document.getElementById('summaryItems'));
      console.log('Desktop button exists:', !!document.getElementById('checkoutBtn'));
      console.log('Mobile button exists:', !!document.getElementById('mobileCheckoutBtn'));
      const items = this.collectItems({ strict: false });
      console.log('Items collected (non-strict):', items);
      console.log('Last synced hash:', this._lastSyncedHash);
      console.log('Syncing:', this._isSyncing);
      console.log('==============================');
    }
  };

  // expose globally for builder to call queueSync() if needed
  window.FamilyPlanCheckout = FamilyPlanCheckout;

  // ---------- bootstrap ----------
  function start() {
    waitForElements('#familyBuilder, #checkoutSummary').then(() => {
      console.log('Builder exists:', !!document.getElementById('familyBuilder'));
      console.log('Summary exists:', !!document.getElementById('summaryItems'));
      console.log('Desktop button:', !!document.getElementById('checkoutBtn'));
      console.log('Mobile button:', !!document.getElementById('mobileCheckoutBtn'));
      FamilyPlanCheckout.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  onShopifySectionEvents(() => FamilyPlanCheckout.reinit());

  console.log('🎯 Family Plan Checkout ready. Type "FamilyPlanCheckout.debugInfo()" in console for debug info.');
})();
